// The items-list inputs of an extended open-ended question, ported from the shared coffee list
// view (ItemsListView#calcItemsListProps) for this family alone.
//
// The coffee view still serves matrix, heatmap, rapid-association, menu-test and the correlation
// finder, so only the branches oex actually reaches are here: an answer-variant row per variant,
// the trait breakdown a row expands into, and the sort the shared s<key> section carries. An
// extended open-ended question is never a pre-qualification question, so no screener marks or
// blanks a row.

import { encodeLabel, formatAmounts } from '@aytm/app/stats/lib/util/helpers'
import { getCustomVariables, getModelValue, getReportVirtual, getSurveyVariables, getTrait, getFeature } from '@aytm/app/stats/lib/stat-page'
import {
  isClickable,
  isRType as calcIsRType,
  isTraitType as calcIsTraitType,
  processText
} from '@aytm/app/stats/page/components/lib/utils'
import { hasBase as answerHasBase } from '@aytm/app/stats/page/question-views/answer-variants/components/chart/lib/chartModes'
import { calcValueCell } from '@aytm/app/stats/page/question-views/answer-variants/lib/table-utils'
import { answerSettled } from '@aytm/app/stats/page/question-views/oe/lib/table-utils'

// the palette repeats every 21 rows, and index 0 is reserved for a trait-coloured row
const COLOR_INDEXES = 21

const colorIndexOf = position => ((position - 1) % COLOR_INDEXES) + 1

// The trait the report is broken down by, wherever it is defined.
const resolveTrait = traitName => {
  if (null == traitName) return null

  return (
    getTrait() ||
    getSurveyVariables('variables')?.[traitName] ||
    getSurveyVariables('pipingVariables')?.[traitName] ||
    getCustomVariables()?.[traitName] ||
    getReportVirtual()[traitName]
  )
}

const traitValuesOf = trait => {
  if (null == trait) return []

  return (trait.values || trait.answerVariants || []).map(value => ({
    id: (Object.hasOwn(value, 'id') && value.id) || (Object.hasOwn(value, 'position') && `a${value.position}`) || value,
    name:
      (Object.hasOwn(value, 'name') && encodeLabel(value.name.en || value.name)) ||
      (Object.hasOwn(value, 'position') && `A${value.position}`) ||
      value
  }))
}

// What the progress column says it is measuring, which depends on what the question counts.
// The extended open-end reports what its figures were taken against rather than naming a count:
// its rows are coded answers, so the respondents behind them are the base, not the tally.
const calcProgressTitle = ({ answerType, answer, hasBase, hideTotal, rType }) => {
  if ('amount' !== answerType) {
    return hideTotal ? '' : `<small>(N=${formatAmounts(answer, 'total', 0)})</small>`
  }

  if (hasBase) return `<small>(based on ${formatAmounts(answer, ['base', 'total'])} responses)</small>`
  if (hideTotal) return ''

  const total = 'cases' === rType ? 'total' : 'respTotal'

  return `<small>(based on ${formatAmounts(answer, total, 0)} responses)</small>`
}

const calcTraitTable = ({ id, trait, traitName, traitValues, answer, answerVariantPosition }) => ({
  id: `${id}-traits`,
  cols: [
    {
      type: 'title',
      title: encodeLabel(
        (Object.hasOwn(trait, 'title') && trait.title) ||
          (Object.hasOwn(trait, 'id') && traitName.toUpperCase()) ||
          traitName
      )
    },
    // the share names itself where the % marker would sit; the column heads no count slot of its own
    { type: 'progress', title: '', percentLabel: '% of this answer', format: value => formatAmounts(value) }
  ],
  rows: traitValues.map((value, index) => ({
    cells: [
      { title: value.name.en || value.name, colorIndex: (index % COLOR_INDEXES) + 1 },
      { amount: answer[value.id]?.amount || 0, percents: answer[value.id]?.percents || 0 }
    ],
    position: index + 1,
    // the chart draws this value as one segment of the answer's own column, and the two find each
    // other by name -- the row's own position numbers the trait value, not the answer
    pointId: `a${answerVariantPosition}t${index + 1}`
  })),
  classNames: ['child']
})

// The column the stored sort names, marked with the direction it is sorted in. An order naming a
// column the list does not offer falls to the last one, the way the coffee list resolved it.
const applySortedColumn = (cols, sortOrder) => {
  const parts = String(sortOrder).split('-')

  const found =
    1 < parts.length
      ? cols.findIndex(col => col.param === parts[0])
      : cols.findIndex(col => (('i' === sortOrder[0] && 'title') || 'progress') === col.type)

  const index = 0 > found ? cols.length - 1 : found
  const direction = 2 === parts.length ? ('asc' === parts[1] && 1) || -1 : ('asc' === sortOrder.slice(1) && 1) || -1

  return cols.map((col, i) => (i === index ? { ...col, sorted: direction } : col))
}

export const calcTableProps = ({
  id,
  questionId,
  type,
  format,
  components,
  answer,
  answerVariants,
  answerType = 'amount',
  sortOrder = 'iasc',
  clickable = true,
  handlers = {}
} = {}) => {
  const variants = (answerVariants || []).filter(av => av)
  if (!variants.length) return { cols: [], rows: [] }

  const isRType = calcIsRType({ type })
  const rType = isRType ? getModelValue('rType') : 'cases'

  const traitName = getModelValue('trait')
  const trait = resolveTrait(traitName)
  const traitValues = traitValuesOf(trait)

  const isTraitType = calcIsTraitType({ format, type })
  const isLoading = 'loading' === answer
  const settled = answerSettled(answer)
  const hasBase = answerHasBase(answer)

  const hideTotal = true === format?.hideTotal
  const ignoreRefs = true === format?.ignoreRefs
  const ignoreTraits = true === format?.ignoreTraits
  const ignore = { amount: true === format?.percentsOnly, percents: true === format?.amountOnly }
  const textAmount = true === format?.textAmount

  const percentDecimals = Object.hasOwn(format || {}, 'percentDecimals')
    ? format.percentDecimals
    : getModelValue('percentDecimals')

  const commentsOn =
    (!components || undefined === components.comments || true === components.comments) &&
    'off' !== getFeature('individual_data')?.status?.oe

  const cols = [
    { type: 'title', title: 'Answers', sort: (aRow, bRow, dir) => dir * (aRow.position - bRow.position) },
    {
      type: 'progress',
      title: calcProgressTitle({ answerType, answer, hasBase, hideTotal, rType }),
      percentDecimals,
      sort: (aRow, bRow, dir) =>
        dir * (aRow.cells[1].percents + aRow.position / 10000 - (bRow.cells[1].percents + bRow.position / 10000)),
      format: value => {
        if (!Number.isFinite(value)) return value

        return textAmount ? value : formatAmounts(value)
      }
    }
  ]

  const rows = variants.map(variant => {
    const variantAnswer = answer?.[`a${variant.position}`] || {}

    const title = {
      title: processText(variant.text),
      colorIndex: trait && isTraitType && !ignoreTraits ? 0 : colorIndexOf(variant.position)
    }

    if (!ignoreRefs) title.label = `A${variant.position}`

    if (Object.hasOwn(variant, 'image')) title.image = variant.image

    if (Object.hasOwn(variant, 'skipsTo')) {
      title.skips_to = variant.skipsTo
      title.onSkipsToClick = handlers.onSkipsToClick
    }

    if (commentsOn && Object.hasOwn(variantAnswer, 'comments')) {
      title.comments = variantAnswer.comments
      title.onCommentsClick = handlers.onCommentsClick
    }

    if (isClickable(format, clickable)) title.onLabelClick = handlers.onLabelClick

    const row = {
      position: variant.position,
      cells: [title, calcValueCell({ variant, variantAnswer, answerType, rType, isLoading, settled, ignore })]
    }

    // a trait breakdown is a table of its own the row expands into
    if (trait && isTraitType && undefined !== variantAnswer[traitName]) {
      row.table = calcTraitTable({
        id,
        trait,
        traitName,
        traitValues,
        answer: variantAnswer[traitName],
        answerVariantPosition: variant.position
      })
    }

    return row
  })

  return { id, questionId, cols: applySortedColumn(cols, sortOrder), rows, onSort: handlers.onSort }
}
